import { useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { annotationsOf, annotationSvgContent, isMeaningfulAnnotation, MAX_ANNOTATIONS, normalizedAnnotation, type AnnotationKind, type PhotoAnnotation } from '../../domain/annotation';
import type { V2Evidence } from '../../domain/evidence';
import { useT } from '../../i18n/i18n';
import { setEvidenceAnnotationsAction } from '../../state/evidenceStore';
import { useMediaUrl } from '../../state/mediaUrls';
import { Button, SegmentedControl, SheetShell } from '../../ui/components';

// The marks over a photo, in the photo's own pixel space so arrows and circles keep their shape.
export function AnnotationOverlay(props: { evidence: Pick<V2Evidence, 'width' | 'height'>; annotations: readonly PhotoAnnotation[]; testId?: string }) {
  const width = props.evidence.width && props.evidence.width > 0 ? props.evidence.width : 1200;
  const height = props.evidence.height && props.evidence.height > 0 ? props.evidence.height : 900;
  if (props.annotations.length === 0) return null;
  return (
    <svg
      className="annotation-overlay"
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      aria-hidden="true"
      data-testid={props.testId}
      data-count={props.annotations.length}
      dangerouslySetInnerHTML={{ __html: annotationSvgContent(props.annotations, width, height) }}
    />
  );
}

// 标注 (Owner decision list 2026-10-19 item 22): draw arrows, circles or boxes on a photo with a finger. The
// photo itself is never changed; the marks are saved on its record and printed on it in the report.
export function AnnotateSheet(props: { evidence: V2Evidence; onClose: () => void }) {
  const { t } = useT();
  const url = useMediaUrl(props.evidence.id, 'full');
  const [tool, setTool] = useState<AnnotationKind>('arrow');
  const [marks, setMarks] = useState<PhotoAnnotation[]>(() => annotationsOf(props.evidence.annotations));
  const [drawing, setDrawing] = useState<PhotoAnnotation | null>(null);
  const [busy, setBusy] = useState(false);
  const surface = useRef<HTMLDivElement | null>(null);

  function pointOf(event: ReactPointerEvent<HTMLDivElement>): { x: number; y: number } | null {
    const rect = surface.current?.getBoundingClientRect();
    if (!rect || rect.width === 0 || rect.height === 0) return null;
    return { x: (event.clientX - rect.left) / rect.width, y: (event.clientY - rect.top) / rect.height };
  }

  function down(event: ReactPointerEvent<HTMLDivElement>) {
    if (marks.length >= MAX_ANNOTATIONS) return;
    const p = pointOf(event);
    if (!p) return;
    event.currentTarget.setPointerCapture?.(event.pointerId);
    setDrawing(normalizedAnnotation(tool, p.x, p.y, p.x, p.y));
  }

  function move(event: ReactPointerEvent<HTMLDivElement>) {
    if (!drawing) return;
    const p = pointOf(event);
    if (p) setDrawing(normalizedAnnotation(drawing.kind, drawing.x1, drawing.y1, p.x, p.y));
  }

  function up() {
    if (drawing && isMeaningfulAnnotation(drawing)) setMarks((prev) => [...prev, drawing]);
    setDrawing(null);
  }

  async function save() {
    setBusy(true);
    try {
      await setEvidenceAnnotationsAction(props.evidence.id, marks);
      props.onClose();
    } finally {
      setBusy(false);
    }
  }

  const shown = drawing ? [...marks, drawing] : marks;
  return (
    <SheetShell
      full
      title={t('annotate.title')}
      subtitle={t('annotate.subtitle')}
      onClose={props.onClose}
      layer="top"
      testId="annotate-sheet"
      footer={
        <>
          <Button onClick={() => void save()} disabled={busy} icon="save" testId="annotate-save">
            {t('annotate.save')}
          </Button>
          <Button variant="text" onClick={props.onClose} testId="annotate-cancel">
            {t('shell.cancel')}
          </Button>
        </>
      }
    >
      <SegmentedControl
        variant="pill"
        block
        ariaLabel={t('annotate.tool')}
        value={tool}
        onChange={(next) => setTool(next)}
        options={[
          { key: 'arrow', label: t('annotate.arrow'), testId: 'annotate-tool-arrow' },
          { key: 'circle', label: t('annotate.circle'), testId: 'annotate-tool-circle' },
          { key: 'box', label: t('annotate.box'), testId: 'annotate-tool-box' }
        ]}
        testId="annotate-tools"
      />
      <div className="annotate-stage">
        <div className="annotate-frame" ref={surface} onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={() => setDrawing(null)} data-testid="annotate-surface">
          {url ? <img src={url} alt="" draggable={false} /> : <span className="annotate-loading">{t('shell.loading')}</span>}
          <AnnotationOverlay evidence={props.evidence} annotations={shown} testId="annotate-marks" />
        </div>
      </div>
      <p className="card-note">{marks.length >= MAX_ANNOTATIONS ? t('annotate.limit', { n: MAX_ANNOTATIONS }) : t('annotate.hint')}</p>
      <div className="card-actions">
        <Button variant="secondary" icon="undo" onClick={() => setMarks((prev) => prev.slice(0, -1))} disabled={marks.length === 0} testId="annotate-undo">
          {t('annotate.undo')}
        </Button>
        <Button variant="text" icon="delete" onClick={() => setMarks([])} disabled={marks.length === 0} testId="annotate-clear">
          {t('annotate.clear')}
        </Button>
      </div>
    </SheetShell>
  );
}
